<template>
  <div class="change-item-in-cart-button">
    <div
      v-show="!isSolitaryItem && isCartEditable"
      class="change-item-in-cart-button__button-container"
    >
      <LinkButton
        ref="decreaseButton"
        type="button"
        tag="button"
        :disabled="!decrementable"
        class="change-item-in-cart-button__button"
        outline
        icon-only
        data-tealium-rel="content.button.produkt-verringern"
        title="Anzahl verringern"
        @click="decrementItemCount"
      >
        -
      </LinkButton>

      <span class="change-item-in-cart-button--current">{{ item.quantity }}</span>

      <LinkButton
        ref="increaseButton"
        type="button"
        tag="button"
        class="change-item-in-cart-button__button"
        :disabled="!incrementable"
        outline
        icon-only
        data-tealium-rel="content.button.produkt-erhoehen"
        title="Anzahl erhöhen"
        @click="incrementItemCount"
      >
        +
      </LinkButton>
    </div>
    <div
      v-show="isSolitaryItem || !isCartEditable"
      class="change-item-in-cart-button__solitary"
    >
      <span class="change-item-in-cart-button--solitary-amount">{{ item.quantity }}</span>
    </div>
    <div
      v-show="isCartEditable"
      class="change-item-in-cart-button__delete"
    >
      <LinkButton
        ref="deleteButton"
        type="button"
        tag="button"
        text
        class="change-item-in-cart-button__delete-btn"
        data-tealium-rel="content.text.produkt-loeschen"
        @click="deleteItem"
      >
        Löschen
      </LinkButton>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { flatten } from 'lodash-es';
import { getErrorMessages } from '~/lib/error-parser';
import serializeCartAction from '../lib/tealium/serializers/cart';
import { LinkButton } from '@i22-td-smarthome/component-library';

export default {
  name: 'ChangeItemInCartButton',
  components: { LinkButton },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  inject: ['isCartEditable'],
  computed: {
    decrementable() {
      return this.item.quantity > 1;
    },
    incrementable() {
      return this.item.quantity < this.maxAmount;
    },
    maxAmount() {
      if (this.item.contractNumber) {
        return 1;
      }
      return this.item.maximumAvailable;
    },
    isSolitaryItem() {
      return this.maxAmount === 1;
    },
  },
  methods: {
    ...mapActions('cart', ['updateCartItems']),
    async deleteItem() {
      this.$flyout.open({
        content: `Willst Du <strong>${this.item.name}</strong> wirklich löschen?`,
        warning: true,
        buttons: [
          {
            name: 'Nein, nicht löschen',
            outline: true,
          },
          {
            tag: 'button',
            name: 'Ja, löschen',
            autofocus: true,
            outline: true,
            click: async (closeModal) => {
              closeModal();
              try {
                await this.updateCartItems([
                  { articleNumber: this.item.articleNumber, quantity: 0 },
                ]);

                await this.$tealium.link({
                  wt_link_id: this.$refs.deleteButton.$el.dataset.tealiumRel,
                  event_name: 'cart_remove',
                  ...serializeCartAction(undefined, [this.item]),
                });
              } catch (error) {
                await this.handleError(error);
              }
            },
          },
        ],
      });
    },
    incrementItemCount() {
      if (!this.incrementable) return;
      this.updateCartQuantity(this.item.quantity + 1);
      this.$tealium.link(this.$refs.increaseButton.$el.dataset.tealiumRel);
    },
    decrementItemCount() {
      if (!this.decrementable) return;
      this.updateCartQuantity(this.item.quantity - 1);
      this.$tealium.link(this.$refs.decreaseButton.$el.dataset.tealiumRel);
    },
    async updateCartQuantity(quantity) {
      try {
        await this.updateCartItems([{ articleNumber: this.item.articleNumber, quantity }]);
      } catch (error) {
        await this.handleError(error);
      }
    },
    async handleError(error) {
      const errors = error?.response?.data?.errors;
      if (!errors) return;
      const fallbackMessage = 'Hoppla! Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.';
      const parsedErrors = flatten(Object.values(getErrorMessages(errors, fallbackMessage)));
      if (parsedErrors.length === 0) return;

      this.$flyout.info(parsedErrors.join('<br>'), { type: 'error' });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.change-item-in-cart-button {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  user-select: none;
  @include mq($mq-md) {
    flex-direction: row;
    align-items: center;
  }

  &__button-container {
    display: flex;
    flex-direction: row;
    @media screen and (min-width: 1024px) {
      margin-right: 12px;
    }
  }

  &__button {
    aspect-ratio: 1;
  }

  &__solitary {
    // width: 105px; // magic number, taken from width of - / + buttons
    text-align: center;
    @media screen and (min-width: 1024px) {
      margin-right: 12px;
    }
  }

  &--current {
    display: flex;
    align-items: center;
    @media screen and (min-width: 0px) {
      padding-right: 12px;
      padding-left: 12px;
    }
  }

  &--solitary-amount {
    text-align: center;

    @media screen and (min-width: 0px) {
      padding-right: 12px;
      padding-left: 12px;
    }
  }

  &__delete {
    display: flex;
    align-items: center;

    &-btn {
      @include font-size(15px, 24px);
    }
  }
}
</style>
